<template>
    <UserModalShell title="Edit user details" test-id="user-details-edit-modal" @cancel="$emit('cancel')">
        <form class="gap-md flex flex-col" @submit.prevent="submit">
            <!-- Single column on mobile: two columns squeeze the inputs until
                 values like email addresses no longer fit (MR !312 review). -->
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                    label-id="first-name"
                    label="First name"
                    :required="true"
                    :error-message="errors.first_name"
                >
                    <input id="first-name" v-model="values.first_name" name="first-name" type="text" />
                </FormField>
                <FormField label-id="last-name" label="Last name" :required="true" :error-message="errors.last_name">
                    <input id="last-name" v-model="values.last_name" name="last-name" type="text" />
                </FormField>
                <FormField label-id="email" label="Email" :required="true" :error-message="errors.email">
                    <input id="email" v-model="values.email" name="email" type="email" />
                </FormField>
                <FormField
                    label-id="phone-number"
                    label="Phone number"
                    :required="false"
                    :error-message="errors.phone_number"
                >
                    <input id="phone-number" v-model="values.phone_number" name="phone-number" type="text" />
                </FormField>
            </div>
            <label
                v-if="props.securityCourseCompleted !== null"
                for="security-course-completed"
                class="gap-sm flex w-fit cursor-pointer items-center"
            >
                <input
                    id="security-course-completed"
                    v-model="securityCourseCompletedValue"
                    type="checkbox"
                    class="size-sm"
                />
                Security course completed
            </label>
            <div
                v-if="isVouchingForSecurityCourse"
                id="security-course-warning"
                class="border-l-4 border-yellow-500 bg-yellow-100 p-4 text-yellow-700"
            >
                <p>
                    By marking this course as completed you are confirming that this user has completed the
                    security course.
                </p>
            </div>
        </form>
        <template #footer>
            <Button type="button" :disabled="!canSubmit" @click="submit">Save changes</Button>
            <button class="bold" type="button" @click="$emit('cancel')">Cancel</button>
        </template>
    </UserModalShell>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { computed, ref, watch } from 'vue';
import z from 'zod';

import UserModalShell from '@/components/manager/user-modal/user-modal-shell.vue';
import { FormField } from '@/components/ui/form-field';
import { useFormValidation } from '@/composables/use-form-validation';

export interface UserDetailsFormValues {
    [key: string]: string;
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
}

const props = withDefaults(
    defineProps<{
        firstName: string | null | undefined;
        lastName: string | null | undefined;
        email: string | null | undefined;
        phoneNumber: string | null | undefined;
        securityCourseCompleted?: boolean | null;
    }>(),
    {
        securityCourseCompleted: null,
    },
);

const emit = defineEmits<{
    cancel: [];
    submit: [payload: UserDetailsFormValues, securityCourseCompleted: boolean | null];
}>();

const formSchema = z.object({
    first_name: z.string().trim().min(1, 'Required'),
    last_name: z.string().trim().min(1, 'Required'),
    email: z.email('Invalid email'),
    phone_number: z.string().optional(),
});

const getInitialValues = (): UserDetailsFormValues => ({
    first_name: props.firstName ?? '',
    last_name: props.lastName ?? '',
    email: props.email ?? '',
    phone_number: props.phoneNumber ?? '',
});

const initialValues = getInitialValues();
const { values, errors, validate, replaceValues } = useFormValidation(formSchema, initialValues);

const securityCourseCompletedValue = ref(props.securityCourseCompleted ?? false);
const isVouchingForSecurityCourse = computed(
    () => securityCourseCompletedValue.value && !(props.securityCourseCompleted ?? false),
);

watch(
    () => [props.firstName, props.lastName, props.email, props.phoneNumber],
    () => {
        replaceValues(getInitialValues());
    },
);

watch(
    () => props.securityCourseCompleted,
    (value) => {
        securityCourseCompletedValue.value = value ?? false;
    },
);

// Save is gated on "something changed", not on validity: pre-existing details that fail the
// schema (e.g. a legacy email or an empty name) must not silently block a security-course-only
// change. submit() runs validate(), which surfaces field errors instead.
const canSubmit = computed(() => {
    const fieldsChanged = Object.entries(getInitialValues()).some(
        ([key, value]) => values[key as keyof UserDetailsFormValues] !== value,
    );
    const securityCourseChanged = securityCourseCompletedValue.value !== (props.securityCourseCompleted ?? false);

    return fieldsChanged || securityCourseChanged;
});

function submit() {
    if (!validate()) {
        return;
    }

    emit(
        'submit',
        {
            first_name: values.first_name.trim(),
            last_name: values.last_name.trim(),
            email: values.email.trim(),
            phone_number: values.phone_number.trim(),
        },
        props.securityCourseCompleted !== null ? securityCourseCompletedValue.value : null,
    );
}
</script>
